import * as React from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Container,
  CssBaseline,
  TextField,
  Typography,
  Paper,
  Divider,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import AppTheme from '../theme/AppTheme';
import ColorModeSelect from '../theme/ColorModeSelect';
import ProfileDropdown from '../components/ProfileDropdown';
import { jsPDF } from 'jspdf';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/firebase';

// Styled components
const BillingContainer = styled(Stack)(({ theme }) => ({
  height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  minHeight: '100%',
  padding: theme.spacing(3),
  position: 'relative',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    // Light mode background
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 95%), hsl(0, 0%, 98%))',
    backgroundRepeat: 'no-repeat',
    // Dark mode background
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

// Interface for the cargo data passed from the analysis page
interface CargoOption {
  mode: string;
  price: string;
  day: string;
}

interface CargoData {
  tons: number;
  from: string;
  to: string;
  selectedOption: CargoOption;
}

// Mock credit card validation
const validateCreditCard = (number: string): boolean => {
  return /^\d{16}$/.test(number);
};

const validateExpiryDate = (date: string): boolean => {
  return /^\d{2}\/\d{2}$/.test(date);
};

const validateCVV = (cvv: string): boolean => {
  return /^\d{3,4}$/.test(cvv);
};

const BillingPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [cargoData, setCargoData] = React.useState<CargoData | null>(null);
  const [paymentMethod, setPaymentMethod] = React.useState('creditCard');
  const [cardNumber, setCardNumber] = React.useState('');
  const [cardExpiry, setCardExpiry] = React.useState('');
  const [cardCVV, setCardCVV] = React.useState('');
  const [cardName, setCardName] = React.useState('');
  const [billingAddress, setBillingAddress] = React.useState('');
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);

  React.useEffect(() => {
    // Get the cargo data from the location state passed by the Analyse page
    const state = location.state as { cargoData?: CargoData };
    if (state?.cargoData) {
      setCargoData(state.cargoData);
    } else {
      // Redirect back to analyse if no data is available
      navigate('/analyse');
    }
  }, [location, navigate]);

  const handlePaymentMethodChange = (event: SelectChangeEvent) => {
    setPaymentMethod(event.target.value);
  };

  const handleProcessPayment = async () => {
    // Validate form
    if (paymentMethod === 'creditCard' && 
        (!validateCreditCard(cardNumber) || 
         !validateExpiryDate(cardExpiry) ||
         !validateCVV(cardCVV) ||
         !cardName)) {
      return; // Don't proceed if validation fails
    }

    if (!billingAddress) return;
    
    setIsProcessing(true);
    
    try {
      // Mock processing delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Store the billing record in Firestore
      if (auth.currentUser && cargoData) {
        await addDoc(collection(db, 'cargo_billings'), {
          uid: auth.currentUser.uid,
          email: auth.currentUser.email,
          cargoData: {
            tons: cargoData.tons,
            from: cargoData.from,
            to: cargoData.to,
            transportMode: cargoData.selectedOption.mode,
            price: cargoData.selectedOption.price,
            estimatedDelivery: cargoData.selectedOption.day,
          },
          paymentMethod,
          billingAddress,
          // Don't store actual card details in a real app!
          paymentStatus: 'Completed',
          timestamp: serverTimestamp(),
        });
      }
      
      // Show success message
      setShowSuccess(true);
    } catch (error) {
      console.error("Error processing payment:", error);
    } finally {
      setIsProcessing(false);
    }
  };
  
  const generateInvoice = () => {
    if (!cargoData) return;
    
    const doc = new jsPDF();
    doc.setFontSize(22);
    doc.text("Cargo Transport Invoice", 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.text(`Invoice Date: ${new Date().toLocaleDateString()}`, 20, 40);
    doc.text(`Invoice ID: INV-${Math.floor(Math.random() * 1000000)}`, 20, 50);
    
    doc.text("Cargo Details:", 20, 70);
    doc.text(`Weight: ${cargoData.tons} ton(s)`, 30, 80);
    doc.text(`From: ${cargoData.from}`, 30, 90);
    doc.text(`To: ${cargoData.to}`, 30, 100);
    doc.text(`Transport Mode: ${cargoData.selectedOption.mode.toUpperCase()}`, 30, 110);
    doc.text(`Estimated Delivery: ${cargoData.selectedOption.day} day(s)`, 30, 120);
    
    doc.text("Payment Summary:", 20, 140);
    
    // Price calculation
    const basePrice = parseInt(cargoData.selectedOption.price.replace(/[^\d]/g, ''));
    const tax = Math.round(basePrice * 0.1);
    const handling = Math.round(basePrice * 0.05);
    const total = basePrice + tax + handling;
    
    doc.text(`Base Price: $${basePrice}`, 30, 150);
    doc.text(`Tax (10%): $${tax}`, 30, 160);
    doc.text(`Handling Fee (5%): $${handling}`, 30, 170);
    doc.text(`Total Amount: $${total}`, 30, 185);
    
    doc.text("Thank you for choosing our Cargo Management System!", 105, 220, { align: 'center' });
    
    doc.save("cargo-transport-invoice.pdf");
  };
  
  if (!cargoData) {
    return (
      <AppTheme>
        <CssBaseline enableColorScheme />
        <BillingContainer>
          <Typography variant="h5">Loading billing information...</Typography>
        </BillingContainer>
      </AppTheme>
    );
  }

  // Calculate price breakdown
  const basePrice = parseInt(cargoData.selectedOption.price.replace(/[^\d]/g, ''));
  const tax = Math.round(basePrice * 0.1);
  const handling = Math.round(basePrice * 0.05);
  const total = basePrice + tax + handling;

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      
      {/* Top Navigation Bar */}
      <AppBar position="static" color="inherit" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Cargo Management System
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
            <ProfileDropdown />
            <ColorModeSelect sx={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 2000 }} />
          </Box>
        </Toolbar>
      </AppBar>

      <BillingContainer>
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Typography 
            component="h1" 
            variant="h4" 
            align="center" 
            sx={{ fontSize: 'clamp(1.8rem, 5vw, 2.15rem)', mb: 4 }}
          >
            Cargo Billing
          </Typography>
          
          <Grid container spacing={4}>
            {/* Left column: Order Summary */}
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 3, mb: { xs: 3, md: 0 } }}>
                <Typography variant="h5" sx={{ mb: 2 }}>Order Summary</Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1">Cargo Details:</Typography>
                  <Typography>
                    {cargoData.tons} ton(s) from <strong>{cargoData.from}</strong> to <strong>{cargoData.to}</strong>
                  </Typography>
                  <Typography sx={{ mt: 1 }}>
                    Transport mode: <strong>{cargoData.selectedOption.mode.toUpperCase()}</strong>
                  </Typography>
                  <Typography>
                    Estimated delivery: <strong>{cargoData.selectedOption.day} day(s)</strong>
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Price Breakdown:</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Base price:</Typography>
                  <Typography>${basePrice}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Tax (10%):</Typography>
                  <Typography>${tax}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography>Handling fee (5%):</Typography>
                  <Typography>${handling}</Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6">${total}</Typography>
                </Box>
              </Paper>
            </Grid>
            
            {/* Right column: Payment Information */}
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ mb: 3 }}>Payment Information</Typography>
                
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="payment-method-label">Payment Method</InputLabel>
                  <Select
                    labelId="payment-method-label"
                    value={paymentMethod}
                    label="Payment Method"
                    onChange={handlePaymentMethodChange}
                  >
                    <MenuItem value="creditCard">Credit Card</MenuItem>
                    <MenuItem value="paypal">PayPal</MenuItem>
                    <MenuItem value="bankTransfer">Bank Transfer</MenuItem>
                  </Select>
                </FormControl>
                
                {paymentMethod === 'creditCard' && (
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      label="Card Number"
                      fullWidth
                      margin="normal"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(e.target.value)}
                      error={cardNumber !== '' && !validateCreditCard(cardNumber)}
                      helperText={cardNumber !== '' && !validateCreditCard(cardNumber) ? "Enter a valid 16-digit card number" : ""}
                      inputProps={{ maxLength: 16 }}
                    />
                    
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <TextField
                        label="Expiry (MM/YY)"
                        value={cardExpiry}
                        onChange={(e) => setCardExpiry(e.target.value)}
                        error={cardExpiry !== '' && !validateExpiryDate(cardExpiry)}
                        helperText={cardExpiry !== '' && !validateExpiryDate(cardExpiry) ? "Format: MM/YY" : ""}
                        inputProps={{ maxLength: 5 }}
                      />
                      <TextField
                        label="CVV"
                        value={cardCVV}
                        onChange={(e) => setCardCVV(e.target.value)}
                        error={cardCVV !== '' && !validateCVV(cardCVV)}
                        helperText={cardCVV !== '' && !validateCVV(cardCVV) ? "3-4 digits" : ""}
                        inputProps={{ maxLength: 4 }}
                      />
                    </Box>
                    
                    <TextField
                      label="Cardholder Name"
                      fullWidth
                      margin="normal"
                      value={cardName}
                      onChange={(e) => setCardName(e.target.value)}
                    />
                  </Box>
                )}
                
                {paymentMethod === 'paypal' && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    You will be redirected to PayPal to complete your payment after clicking "Process Payment".
                  </Alert>
                )}
                
                {paymentMethod === 'bankTransfer' && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Bank transfer details will be provided after clicking "Process Payment".
                  </Alert>
                )}
                
                <TextField
                  label="Billing Address"
                  fullWidth
                  multiline
                  rows={3}
                  margin="normal"
                  value={billingAddress}
                  onChange={(e) => setBillingAddress(e.target.value)}
                  sx={{
                    '& .MuiInputBase-inputMultiline': {
                      padding: '8px 0',
                      lineHeight: 0.8,
                      verticalAlign: 'top',
                      alignSelf: 'flex-start'
                    },
                    '& .MuiOutlinedInput-root': {
                      alignItems: 'flex-start'
                    }
                  }}
                  InputProps={{
                    sx: {
                      alignItems: 'flex-start',
                      padding: '8px 14px 8px 14px'
                    }
                  }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                  <Button variant="outlined" onClick={() => navigate('/analyse')}>
                    Back to Analysis
                  </Button>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    disabled={isProcessing} 
                    onClick={handleProcessPayment}
                    sx={{
                      position: 'relative',
                      minWidth: '120px',
                    }}
                  >
                    {isProcessing ? 'Processing...' : `Pay $${total}`}
                    {isProcessing && (
                      <CircularProgress 
                        size={20} 
                        color="primary" 
                        sx={{
                          position: 'absolute',
                          transform: 'translate(-50%, -50%)'
                        }}
                      />
                    )}
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </BillingContainer>
      
      {/* Success Message */}
      <Snackbar 
        open={showSuccess} 
        autoHideDuration={6000} 
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Payment successful! 
          <Button 
            color="inherit" 
            size="small" 
            onClick={generateInvoice}
          >
            Download Invoice
          </Button>
        </Alert>
      </Snackbar>
    </AppTheme>
  );
};

export default BillingPage;